/**
 * §9.22 / ADR 0010 — the G-11 KPI set.
 *
 * Ten figures. The portal computes two of them from data it already holds; the other
 * eight live in systems it links to but does not query, so they are entered at the
 * monthly review and stored with their provenance.
 *
 * Cadence and direction are properties of the *metric*, identical for every project, so
 * they live here rather than in the database — the same reason freshness days and
 * roll-up precedence live here. `today` is a parameter, never the ambient clock.
 */

import { startOfUtcDay } from './dates.js';

/** The eight figures a human enters. The two the portal computes are `ComputedKpi`. */
export type KpiMetric =
  | 'AVAILABILITY'
  | 'SLA_ATTAINMENT'
  | 'OPEN_CRITICAL_HIGH_VULNS'
  | 'MTTP_CRITICAL_CVE'
  | 'COVERAGE_NEW_CODE'
  | 'QUALITY_GATE_PASS_RATE'
  | 'BACKUP_SUCCESS_RATE'
  | 'INCIDENTS_BY_SEVERITY';

/** The two the portal derives itself. Present so the dashboard can label provenance. */
export type ComputedKpi = 'OPEN_FINDINGS_PAST_DEADLINE' | 'ACTUAL_RTO_RPO';

export type KpiCadence = 'MONTHLY' | 'QUARTERLY';

/**
 * Which way is good. Availability higher, open vulnerabilities lower — without this the
 * dashboard cannot say whether 7 is a good number.
 */
export type KpiDirection = 'HIGHER_IS_BETTER' | 'LOWER_IS_BETTER';

export type KpiVerdict = 'ON_TARGET' | 'OFF_TARGET' | 'NO_TARGET';

export interface KpiMetricSpec {
  readonly metric: KpiMetric;
  readonly label: string;
  readonly unit: string;
  readonly cadence: KpiCadence;
  readonly direction: KpiDirection;
  /** The system a reading is expected to come from, used to pre-fill the entry form. */
  readonly expectedSource: string;
  /** How many decimals the tile shows. Availability needs two; a count needs none. */
  readonly precision: number;
}

/**
 * Straight from the G-11 table in `Project_Checklist.md` §10 — six monthly, two
 * quarterly. The two quarterly ones are why a tile's "as of" is not always this month.
 */
export const KPI_METRICS: readonly KpiMetricSpec[] = [
  {
    metric: 'AVAILABILITY',
    label: 'Availability',
    unit: '%',
    cadence: 'MONTHLY',
    direction: 'HIGHER_IS_BETTER',
    expectedSource: 'AZURE',
    precision: 2,
  },
  {
    metric: 'SLA_ATTAINMENT',
    label: 'SLA attainment, P1–P3',
    unit: '%',
    cadence: 'MONTHLY',
    direction: 'HIGHER_IS_BETTER',
    expectedSource: 'ZAMMAD',
    precision: 1,
  },
  {
    metric: 'OPEN_CRITICAL_HIGH_VULNS',
    label: 'Open critical / high vulnerabilities',
    unit: 'count',
    cadence: 'MONTHLY',
    direction: 'LOWER_IS_BETTER',
    expectedSource: 'DEPENDENCY_TRACK',
    precision: 0,
  },
  {
    metric: 'MTTP_CRITICAL_CVE',
    label: 'Mean time to patch a critical CVE',
    unit: 'days',
    cadence: 'QUARTERLY',
    direction: 'LOWER_IS_BETTER',
    expectedSource: 'DEPENDENCY_TRACK',
    precision: 1,
  },
  {
    metric: 'COVERAGE_NEW_CODE',
    label: 'Test coverage on new code',
    unit: '%',
    cadence: 'MONTHLY',
    direction: 'HIGHER_IS_BETTER',
    expectedSource: 'SONARQUBE',
    precision: 1,
  },
  {
    metric: 'QUALITY_GATE_PASS_RATE',
    label: 'Quality gate pass rate',
    unit: '%',
    cadence: 'MONTHLY',
    direction: 'HIGHER_IS_BETTER',
    expectedSource: 'SONARQUBE',
    precision: 1,
  },
  {
    metric: 'BACKUP_SUCCESS_RATE',
    label: 'Backup success rate',
    unit: '%',
    cadence: 'MONTHLY',
    direction: 'HIGHER_IS_BETTER',
    expectedSource: 'AZURE',
    precision: 1,
  },
  {
    metric: 'INCIDENTS_BY_SEVERITY',
    label: 'Incidents by severity',
    unit: 'count',
    cadence: 'QUARTERLY',
    direction: 'LOWER_IS_BETTER',
    expectedSource: 'ZAMMAD',
    precision: 0,
  },
];

const BY_METRIC = new Map<KpiMetric, KpiMetricSpec>(
  KPI_METRICS.map((spec) => [spec.metric, spec]),
);

/**
 * Throws rather than returning undefined: an unknown metric is a programming error, not
 * a user input — the Zod contract rejects a bad string long before this.
 */
export function kpiSpec(metric: KpiMetric): KpiMetricSpec {
  const spec = BY_METRIC.get(metric);
  if (spec === undefined) {
    throw new Error(`Unknown KPI metric: ${String(metric)}`);
  }
  return spec;
}

/**
 * The period a reading belongs to: `2026-09` for a monthly metric, `2026-Q3` for a
 * quarterly one. The shape encodes the cadence, so a monthly figure can never be filed
 * against a quarter by accident.
 */
export function periodFor(metric: KpiMetric, today: Date): string {
  const day = startOfUtcDay(today);
  const year = day.getUTCFullYear();
  const month = day.getUTCMonth(); // 0-based

  if (kpiSpec(metric).cadence === 'QUARTERLY') {
    return `${String(year)}-Q${String(Math.floor(month / 3) + 1)}`;
  }
  return `${String(year)}-${String(month + 1).padStart(2, '0')}`;
}

const MONTHLY_PERIOD = /^(\d{4})-(0[1-9]|1[0-2])$/;
const QUARTERLY_PERIOD = /^(\d{4})-Q([1-4])$/;

/**
 * True when `period` has the right shape for the metric's cadence. The API validates
 * this before writing, because `@@unique([projectId, period, metric])` would otherwise
 * happily store `2026-Q3` availability alongside `2026-09` availability and the series
 * would silently split in two.
 */
export function isValidPeriod(metric: KpiMetric, period: string): boolean {
  return kpiSpec(metric).cadence === 'QUARTERLY'
    ? QUARTERLY_PERIOD.test(period)
    : MONTHLY_PERIOD.test(period);
}

/**
 * Does this reading meet its target?
 *
 * `NO_TARGET` is a real answer, not a failure: G-11 leaves the target column blank, so a
 * project that has not set one gets its reading shown without a verdict rather than
 * judged against a threshold nobody agreed to.
 */
export function judgeReading(input: {
  readonly metric: KpiMetric;
  readonly value: number;
  readonly target: number | null;
}): KpiVerdict {
  if (input.target === null) {
    return 'NO_TARGET';
  }
  const meets =
    kpiSpec(input.metric).direction === 'HIGHER_IS_BETTER'
      ? input.value >= input.target
      : input.value <= input.target;

  return meets ? 'ON_TARGET' : 'OFF_TARGET';
}

export interface KpiReadingView {
  readonly metric: KpiMetric;
  readonly period: string;
  readonly value: number;
  readonly target: number | null;
}

export interface KpiTileView {
  readonly metric: KpiMetric;
  readonly spec: KpiMetricSpec;
  /** null when nobody recorded this metric for any period yet. */
  readonly latest: KpiReadingView | null;
  readonly verdict: KpiVerdict;
  /**
   * True when the metric is due for `period` and no reading exists for it. Distinct from
   * `latest === null`: a quarterly metric viewed in a month it is not due is neither
   * missing nor stale, it is simply not due.
   */
  readonly missingForPeriod: boolean;
  /**
   * Set when the newest reading belongs to an earlier period than the one being viewed —
   * the tile shows the value with the period it actually describes.
   */
  readonly asOfEarlierPeriod: string | null;
}

/**
 * Builds the eight recorded tiles for a period, given every reading a project has.
 *
 * Deliberately does not carry a value forward: a missed month is a gap in the series
 * (ADR 0010), so a tile that is due and unrecorded shows nothing at all.
 */
export function kpiTiles(input: {
  readonly readings: readonly KpiReadingView[];
  readonly period: string;
  readonly today: Date;
}): readonly KpiTileView[] {
  return KPI_METRICS.map((spec) => {
    const mine = input.readings
      .filter((r) => r.metric === spec.metric)
      .slice()
      .sort((a, b) => (a.period < b.period ? 1 : a.period > b.period ? -1 : 0));

    const forPeriod = mine.find((r) => r.period === input.period) ?? null;
    const latest = forPeriod ?? mine[0] ?? null;

    // A metric is "due" for the period only if the period matches its own cadence shape.
    const due = isValidPeriod(spec.metric, input.period);

    return {
      metric: spec.metric,
      spec,
      latest,
      verdict:
        latest === null
          ? 'NO_TARGET'
          : judgeReading({ metric: spec.metric, value: latest.value, target: latest.target }),
      missingForPeriod: due && forPeriod === null,
      asOfEarlierPeriod: latest !== null && latest.period !== input.period ? latest.period : null,
    };
  });
}
